import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTestList } from "../../../../utils/api-communication/RestApiCli";
import {
  Box,
  Divider,
  Grid,
  Stack,
  Text,
  SimpleGrid,
  LoadingOverlay,
  Flex,
  Container,
} from "@mantine/core";
import GpaReportHeader from "../common/OpaReportHeader";
import StatisticsLegand from "../../comon/StatisticsLegand";
import BaselinePage from "./BaselinePage";
import FollowUpSection from "./sections/FollowUpSection";
import DeteriorationLegand from "../common/DeteriorationLegand";
import {
  buildGpaValues,
  calculateDeviationParameters,
  calculateDeviationProbabilityMaps,
} from "../utils/GpaCalculations";
import { calculateAgeForTestFloat, parseDateOnly } from "../../../../utils/CommonUtil";
import ReportPrintButton from "../common/ReportPrintButton";

export default function FollowUpPage(_props) {
  const componentRefs = useRef([]);

  const { t } = useTranslation();
  const [eye, setEye] = useState(null);
  const [type, setType] = useState(null);
  const [tests, setTests] = useState([]);
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [patientId, setPatientId] = useState("");
  const [values, setValues] = useState([]);
  const [baselinePdAvarageValues, setBaselinePdAvarageValues] = useState([]);
  const [deviationMaps, setDeviationMaps] = useState([]);
  const [deviationProbabilityMaps, setDeviationProbabilityMaps] = useState([]);
  const [ready, setReady] = useState(false);
  const [vfis, setVfis] = useState([]);
  const [unavailableCount, setUnavailableCount] = useState(0);

  const addToRefs = (index) => (el) => {
    if (el) {
      componentRefs.current[index] = el;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uuid = params.get("uuid");
    if (uuid && uuid !== "") {
      getTestList({ uuid: uuid, ascending: true }, (response) => {
        const tests = response.data?.data ?? null;
        if (!tests) return;
        setTests(tests);
        const test = tests[0];
        setEye(test?.eye ?? null);
        setType(test?.testType ?? null);
        setName(test?.patient.name ?? "");
        setDob(test?.patient.dob ?? "");
        setPatientId(test?.patient.uuid ?? "");
      });
    }
  }, []);

  useEffect(() => {
    if (!tests?.length) return;
    const { values: tempValues, unavailable } = buildGpaValues(tests);
    setUnavailableCount(unavailable);
    setValues(tempValues);
  }, [tests]);

  useEffect(() => {
    const result = calculateDeviationParameters(values);
    if (!result) return;
    setBaselinePdAvarageValues(result.baselinePdAvarageValues);
    setDeviationMaps(result.deviationMaps);
    const tempStatisticMaps = calculateDeviationProbabilityMaps(result.deteriorationMaps);
    setDeviationProbabilityMaps(tempStatisticMaps);
    setReady(true);

    const tempVfis = values.map((val) => {
      return {
        age: calculateAgeForTestFloat(new Date(val.startDate), parseDateOnly(val.patient.dob)),
        vfi: val.vfi,
      };
    });
    setVfis(tempVfis);
  }, [values]);

  const divider = <Divider size={"md"} variant="dashed" color="gray.6" />;
  const headerText = (text) => (
    <Text ta="center" fw={600} size={"lg"} c="dimmed">
      {text}
    </Text>
  );

  return (
    <Stack>
      <Container size={"xl"}>
        <Box pos={"relative"}>
          <LoadingOverlay
            visible={!ready && unavailableCount === 0}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
            loaderProps={{ size: "xl" }}
          />
          <Flex justify={"flex-end"} align={"center"} px={"lg"} pt={"md"}>
            <ReportPrintButton componentRefs={Object.values(componentRefs.current)} />
          </Flex>
          <Box ref={addToRefs(0)}>
            <BaselinePage calculatedValues={values.slice(0, 2)} vfis={vfis} />
          </Box>
          <Box p={"lg"} ref={addToRefs(1)}>
            <GpaReportHeader
              title={"Follow Up"}
              name={name}
              dob={dob}
              type={type}
              uuid={patientId}
              eye={eye}
            />
            {unavailableCount > 0 && (
              <Text c="red.6">
                {t("OpaReports.metricsUnavailableWarningText", { count: unavailableCount })}
              </Text>
            )}
            <Stack my={"sm"}>
              <Grid>
                <Grid.Col span={3}>{headerText(t("grayTone"))}</Grid.Col>
                <Grid.Col span={3}>{headerText(t("patternDeviation"))}</Grid.Col>
                <Grid.Col span={3}>{headerText(t("deviationFromBaseline"))}</Grid.Col>
                <Grid.Col span={3}>{headerText(t("progressionAnalysis"))}</Grid.Col>
              </Grid>
              {values.length > 2 &&
                values?.slice(-3).map((test, index) => (
                  <>
                    <FollowUpSection
                      test={test}
                      baselinePdAverages={baselinePdAvarageValues}
                      deviation={deviationMaps.slice(-3)[index]}
                      deviationProbability={deviationProbabilityMaps.slice(-3)[index]}
                    />
                    {divider}
                  </>
                ))}
              <SimpleGrid cols={3}>
                <Box>
                  <Text>{t("OpaReports.baselineExams")}</Text>
                  <Stack>
                    <Text>{tests[0]?.startDate.split("T")[0]}</Text>
                    <Text>{tests[0]?.startDate.split("T")[0]}</Text>
                  </Stack>
                </Box>
                <StatisticsLegand cols={1} />
                <DeteriorationLegand cols={1} />
              </SimpleGrid>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Stack>
  );
}
